import React, {useEffect, useMemo, useState} from 'react';
import './App.css';
import {Route, Routes, useLocation} from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import {UnauthorizedComponent} from './components/unauthorized/unauthorized-component';
import {useStores} from './components/store';
import usePromiseFeedback from './components/shared/promise-feedback/usePromiseFeedback';
import Loader from './components/shared/loader/loader';
import {observer} from 'mobx-react-lite';
import {AppRouter} from './components/layout/app-content/app-router';
import PlanAndUpgrade from './components/planAndUpgrade/plan-and-upgrade';
import {LevelName} from './utils/enums';
import {AppFooter} from './components/layout/app-footer/app-footer';
import {SystemConfiguration} from './utils/system-configuration';
import {useUserActivityTimer} from './utils/userActivityUtil';
import {LogoutState} from './components/logout-state/logout-state';
import {UnsubscribePage} from './components/unsubscribe/unsubscribe-page';
import {UnsubscribeFailedPage} from './components/unsubscribe/unsubscribe-failed-page';
import {EmailLinkSource} from './dtos/analytics.dto';
import {STUB_ID_KEY, STUB_ID_QUERY_PARAM} from './utils/constants';

export const App: React.FC = observer(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const rootStore = useStores();
    useUserActivityTimer();
    const { userStore, customerStore, uiStore } = rootStore;
    const [isAuthenticated, setIsAuthenticated] = useState(userStore.isAuthenticated);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isLoading, setIsLoading] = useState(true);

    const getDefaultRoute = () => {
        return '/select-customer';
    };

    const defaultRoute = getDefaultRoute();

    function isUnauthorizedUser() {
        return userStore.isUnauthorizedUser();
    }

    const isPathExcluded = useMemo(() => {
        return location.pathname.includes('/unauthorized')
            || location.pathname.includes('/logged-out')
            || location.pathname.includes('/unsubscribe');
    }, [location.pathname]);

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const params = ['utm_source', 'utm_medium', 'utm_campaign'];
        const queryParams = params.filter(param => query.has(param));
        let shouldReplaceUrl = false;

        if (queryParams.length === 3) {
            let source = EmailLinkSource.NONE;
            if (
                query.get('utm_source') === 'mail' &&
                query.get('utm_medium') === 'reminder'
            ) {
                if (query.get('utm_campaign') === 'policyRecommendation') {
                    source = EmailLinkSource.RECOMMEND_APP;
                } else if (query.get('utm_campaign') === 'mlRecommendation') {
                    source = EmailLinkSource.RECOMMENDATION_ML;
                } else if (query.get('utm_campaign') === '7_days_no_apply') {
                    source = EmailLinkSource.SEVEN_DAYS_NO_APPLY;
                }
            }
            uiStore.setEmailLinkSource(source);
            params.forEach(p => query.delete(p));
            shouldReplaceUrl = true;
        }

        const stubId = query.get(STUB_ID_QUERY_PARAM);
        if (stubId) {
            sessionStorage.setItem(STUB_ID_KEY, stubId);
            query.delete(STUB_ID_QUERY_PARAM);
            shouldReplaceUrl = true;
        }

        if (shouldReplaceUrl) {
            const remaining = query.toString();
            navigate(remaining ? `${location.pathname}?${remaining}` : location.pathname, { replace: true });
        }
    }, []);

    useEffect(() => {
        if (!userStore.isAuthenticated && !isPathExcluded) {
            promiseFeedbackFactory({
                promise: userStore.load(),
                successCallback: () => {
                    initialSystemConfiguration();
                    setIsAuthenticated(true);
                    const singleCustomerId = userStore.getTheOnlyCustomerOfExternalUser;
                    if (singleCustomerId) {
                        void customerStore.setCustomerById(singleCustomerId);
                        const singleService = userStore.getSingleServiceOfSelectedCustomerIfExist(singleCustomerId);
                        if (singleService) {
                            customerStore.setSelectedLocation(singleService);
                        }
                    }
                },
                notifObj: { show: false }
            });

        } else {
            setIsLoading(false);
        }
    }, [isPathExcluded]);

    const initialSystemConfiguration = () => {
        const systemConfig = SystemConfiguration.getInstance();
        promiseFeedbackFactory({
            promise: systemConfig.load(),
            notifObj: { show: false },
            successCallback: () => {
                uiStore.setSystemDataLoaded();
                setIsLoading(false);
            }
        });
    };

    if (!userStore.isAuthenticated && !isPathExcluded) {
        return <>
            <Loader type="full-screen" />
            {promiseContextHolder}
        </>;
    }

    return <>{!isLoading &&
            <>
                <Routes>
                    <Route path="/unauthorized" element={<UnauthorizedComponent/>}/>
                    <Route path="/logged-out" element={<LogoutState/>}/>
                    <Route path="/unsubscribe" element={<UnsubscribePage/>}/>
                    <Route path="/unsubscribe-failed" element={<UnsubscribeFailedPage/>}/>
                    <Route path="/*" element={<>
                        {isAuthenticated && isUnauthorizedUser() ? <UnauthorizedComponent/> :
                            <AppRouter defaultRoute={defaultRoute}/>}
                    </>}>
                    </Route>
                    <Route path="/plan-upgrade-bronze" element={
                        <PlanAndUpgrade serviceLevel={LevelName.BRONZE}/>
                    }/>
                    <Route path="/plan-upgrade-silver" element={
                        <PlanAndUpgrade serviceLevel={LevelName.SILVER}/>
                    }/>
                    <Route path="/plan-upgrade" element={<PlanAndUpgrade/>
                    }/>
                </Routes>
                <AppFooter />
            </>}
            {promiseContextHolder}
            </>;
});

export default App;
